import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import {
  fetchOffers,
  addOffer,
  updateOffer,
  deleteOffer,
} from '../../store/slices/offersSlice';
import { Edit, Trash2, X, Package, Eye, Upload, Image, Copy } from 'lucide-react';
import { formatCurrency, getImageUrl, saveImageToServer, normalizeMoneyInput, formatMarkupPercent, formatSignedCurrency, formatSkuDisplay } from '../../utils/format';
import { exportOffersToExcel } from '../../utils/excelExport';
import { formatProductUnitType } from '../../utils/unitLabels';
import { normalizePurchaseQuantityInput } from '../../utils/purchaseQuantity';
import { subscribeToOffersUpdates } from '../../services/socket';
import apiService from '../../services/api';
import { getTokens } from '../../utils/tokens';
import { getAdminSelectCSS } from '../../utils/adminSelect';
import {
  DataTable,
  Pagination,
  MonoMoney,
  StatusPill,
  Toolbar,
  StatStrip,
} from '../ui/AdminPagePrimitives';
import { TableSkeleton, StatStripSkeleton } from '../ui/Skeleton';

const OFFER_NAME_MAX_LENGTH = 35;

const rowUnitType = (unitType) => (unitType === 'weight' || unitType === 'kg' ? 'kg' : 'piece');

const productToComponent = (prod) => ({
  product_id: prod.id,
  quantity: '1',
  product_name: prod.name || prod.product_name || '',
  sku: prod.sku || '',
  unit_type: prod.unit_type || 'piece',
  cost_price: parseFloat(prod.cost_price) || 0,
});

const OffersPanel = forwardRef(({ canManage }, ref) => {
  const dispatch = useDispatch();
  const { items: offers, loading } = useSelector((state) => state.offers);
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const tk = getTokens(mode);
  const filterSelect = getAdminSelectCSS(tk, 'offer-filter');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deletingOffer, setDeletingOffer] = useState(null);
  const [viewingOffer, setViewingOffer] = useState(null);
  const [skuCopied, setSkuCopied] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [productSearchInput, setProductSearchInput] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchError, setProductSearchError] = useState(null);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    selling_price: '',
    is_active: true,
    image_url: '',
    items: [],
  });

  const itemsPerPage = 20;

  useEffect(() => {
    dispatch(fetchOffers());
    const unsubOffers = subscribeToOffersUpdates(() => dispatch(fetchOffers()));
    return () => {
      unsubOffers();
    };
  }, [dispatch]);

  const resetProductSearch = () => {
    setProductSearchInput('');
    setProductSearchResults([]);
    setProductSearchError(null);
    setProductSearchLoading(false);
  };

  const closeModal = () => {
    setShowModal(false);
    resetProductSearch();
  };

  const handleAddProductFromSearch = (prod) => {
    if (!prod?.id) return;
    let wasDuplicate = false;
    setFormData((prev) => {
      if (prev.items.some((it) => String(it.product_id) === String(prod.id))) {
        wasDuplicate = true;
        return prev;
      }
      return {
        ...prev,
        items: [...prev.items, productToComponent(prod)],
      };
    });
    if (wasDuplicate) {
      showToast(t('offers.productAlreadyInOffer'), 'warning');
      return;
    }
    resetProductSearch();
  };

  const handleProductSearch = async (e) => {
    e?.preventDefault();
    const q = productSearchInput.trim();
    if (!q) return;
    setProductSearchLoading(true);
    setProductSearchError(null);
    setProductSearchResults([]);
    try {
      try {
        const res = await apiService.getProductByBarcode(q);
        const prod = res?.data || res;
        if (prod?.id) {
          handleAddProductFromSearch(prod);
          return;
        }
      } catch (_) {
        // not found by barcode, fall through to name/SKU search
      }
      const res = await apiService.searchProducts(q);
      const list = Array.isArray(res?.data) ? res.data : [];
      if (list.length === 1) {
        handleAddProductFromSearch(list[0]);
      } else if (list.length === 0) {
        setProductSearchError(t('offers.productNotFound'));
      } else {
        setProductSearchResults(list);
      }
    } catch (err) {
      setProductSearchError(t('offers.productNotFound'));
    } finally {
      setProductSearchLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const hasActiveFilters = searchTerm.trim() !== '' || statusFilter !== 'active';

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('active');
  };

  const inputStyle = {
    width: '100%', height: 40, background: tk.sunken,
    border: `1px solid ${tk.border}`, borderRadius: 8,
    padding: '0 12px', color: tk.text, fontFamily: 'inherit', fontSize: 13, outline: 'none',
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: tk.textMuted,
    textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6, display: 'block',
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };

  const modalBox = {
    background: tk.surface,
    border: `1px solid ${tk.border}`,
    borderRadius: 14,
    padding: 0,
    maxWidth: 644,
    width: '100%',
    margin: '0 16px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: tk.shadowLg,
  };

  const modalCloseBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    background: 'transparent',
    border: `1px solid ${tk.border}`,
    color: tk.textSecondary,
    cursor: 'pointer',
  };

  const previewCost = useMemo(() => {
    let total = 0;
    for (const row of formData.items) {
      if (!row.product_id) continue;
      total += (parseFloat(row.cost_price) || 0) * (parseFloat(row.quantity) || 0);
    }
    return Math.round(total * 100) / 100;
  }, [formData.items]);

  const formMarginAmount = useMemo(
    () => (parseFloat(formData.selling_price) || 0) - previewCost,
    [formData.selling_price, previewCost],
  );

  const filtered = useMemo(() => {
    let list = offers || [];
    if (statusFilter === 'active') {
      list = list.filter((o) => o.is_active !== false);
    } else if (statusFilter === 'inactive') {
      list = list.filter((o) => o.is_active === false);
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter((o) =>
        (o.name || '').toLowerCase().includes(q)
        || (o.sku || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [offers, searchTerm, statusFilter]);

  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

  const offerStats = useMemo(() => {
    const list = offers || [];
    let active = 0;
    let inactive = 0;
    for (const o of list) {
      if (o.is_active === false) inactive += 1;
      else active += 1;
    }
    return { total: list.length, active, inactive };
  }, [offers]);

  const openCreate = () => {
    setEditingOffer(null);
    resetProductSearch();
    setFormData({ name: '', sku: '', selling_price: '', is_active: true, image_url: '', items: [] });
    setShowModal(true);
  };

  const handleExport = async () => {
    if (!filtered.length) return;
    const labels = t('offers.excelExport');
    const L = typeof labels === 'object' && labels !== null ? labels : {};
    const today = new Date().toISOString().split('T')[0];
    await exportOffersToExcel(filtered, `offers-${today}.xlsx`, {
      ...L,
      formatStatus: (isActive) => (isActive !== false ? t('settings.active') : t('settings.inactive')),
      formatUnitType: (u) => formatProductUnitType(u, t),
      noComponents: t('offers.noComponents'),
    });
  };

  useImperativeHandle(ref, () => ({ openCreate, exportExcel: handleExport }));

  const openEdit = (offer) => {
    setEditingOffer(offer);
    resetProductSearch();
    const mappedItems = (offer.items || offer.components || []).map((it) => {
      const ut = rowUnitType(it.unit_type);
      return {
        product_id: it.product_id,
        quantity: normalizePurchaseQuantityInput(String(it.quantity ?? 1), ut) || '1',
        product_name: it.product_name || '',
        sku: it.sku || '',
        unit_type: it.unit_type || 'piece',
        cost_price: parseFloat(it.cost_price) || 0,
      };
    });
    setFormData({
      name: offer.name || '',
      sku: offer.sku || '',
      selling_price: String(offer.selling_price ?? ''),
      is_active: offer.is_active !== false,
      image_url: offer.image_url || offer.image_path || '',
      items: mappedItems,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const name = formData.name.trim();
    if (!name) {
      showToast(t('offers.nameRequired'), 'warning');
      return;
    }
    if (name.length > OFFER_NAME_MAX_LENGTH) {
      showToast(t('offers.nameMaxLength'), 'warning');
      return;
    }
    const selling = parseFloat(formData.selling_price);
    if (!Number.isFinite(selling) || selling <= 0) {
      showToast(t('offers.sellingPriceRequired'), 'warning');
      return;
    }
    const normalizedItems = formData.items
      .filter((it) => it.product_id)
      .map((it) => {
        const unitType = rowUnitType(it.unit_type);
        const qty = normalizePurchaseQuantityInput(it.quantity, unitType);
        return { product_id: it.product_id, quantity: parseFloat(qty) || 0 };
      })
      .filter((it) => it.quantity > 0);
    if (!normalizedItems.length) {
      showToast(t('offers.componentsRequired'), 'warning');
      return;
    }

    const payload = {
      name,
      sku: formData.sku.trim() || null,
      selling_price: selling,
      is_active: formData.is_active,
      image_url: formData.image_url || null,
      items: normalizedItems,
    };

    try {
      if (editingOffer) {
        await dispatch(updateOffer({ id: editingOffer.id, ...payload })).unwrap();
        showToast(t('offers.updated'), 'success');
      } else {
        await dispatch(addOffer(payload)).unwrap();
        showToast(t('offers.created'), 'success');
      }
      closeModal();
      dispatch(fetchOffers());
    } catch (e) {
      const errMsg = typeof e === 'string' ? e : (e?.error || e?.message);
      let showError = t('alerts.failedToSave').replace('{error}', errMsg || t('alerts.unknownError'));
      if (errMsg && String(errMsg).includes('SKU') && String(errMsg).includes('already exists')) {
        showError = t('alerts.failedToSave').replace('{error}', t('offers.skuAlreadyExists'));
      }
      showToast(showError, 'warning');
    }
  };

  const handleDelete = async (offer) => {
    try {
      await dispatch(deleteOffer(offer.id)).unwrap();
      setDeletingOffer(null);
      showToast(t('offers.deleted'), 'success');
      dispatch(fetchOffers());
    } catch (e) {
      showToast(typeof e === 'string' ? e : e?.error || t('alerts.deleteFailed'), 'warning');
    }
  };

  const handleToggleActive = async (offer) => {
    const nextActive = offer.is_active === false;
    setTogglingId(offer.id);
    try {
      const updated = await dispatch(updateOffer({ id: offer.id, is_active: nextActive })).unwrap();
      if (viewingOffer?.id === offer.id) {
        setViewingOffer(updated);
      }
      showToast(nextActive ? t('offers.activated') : t('offers.deactivatedStatus'), 'success');
      dispatch(fetchOffers());
    } catch (e) {
      showToast(typeof e === 'string' ? e : e?.error || t('alerts.saveFailed'), 'warning');
    } finally {
      setTogglingId(null);
    }
  };

  const actionBtnStyle = (bg, border, color) => ({
    width: 28,
    height: 28,
    flexShrink: 0,
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color,
  });

  const renderOfferStatus = (offer) => {
    const isActive = offer.is_active !== false;
    if (!canManage) {
      return (
        <StatusPill tk={tk} kind={isActive ? 'active' : 'closed'}>
          {isActive ? t('settings.active') : t('settings.inactive')}
        </StatusPill>
      );
    }
    const toggling = togglingId === offer.id;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={isActive}
        onClick={() => handleToggleActive(offer)}
        disabled={toggling}
        title={isActive ? t('offers.deactivate') : t('offers.activate')}
        style={{
          width: 34,
          height: 20,
          padding: 2,
          borderRadius: 999,
          border: 'none',
          background: isActive ? tk.success : tk.sunken,
          boxShadow: isActive ? 'none' : `inset 0 0 0 1px ${tk.border}`,
          cursor: toggling ? 'not-allowed' : 'pointer',
          opacity: toggling ? 0.6 : 1,
          display: 'inline-flex',
          alignItems: 'center',
          // flex-end/flex-start follow text direction, so the knob mirrors in RTL.
          justifyContent: isActive ? 'flex-end' : 'flex-start',
          transition: 'background 0.15s ease',
          verticalAlign: 'middle',
          flexShrink: 0,
        }}
      >
        <span style={{
          display: 'block',
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
        }} />
      </button>
    );
  };

  const columns = [
    { key: 'sku', label: t('offers.sku') },
    { key: 'name', label: t('offers.offerName') },
    { key: 'preview_cost', label: t('offers.previewCost') },
    { key: 'selling_price', label: t('inventory.sellingPrice') },
    { key: 'margin', label: t('inventory.profitMargin') },
    { key: 'status', label: t('settings.userStatus') },
    { key: 'actions', label: '' },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case 'name':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 6, flexShrink: 0,
              background: tk.sunken, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {row.image_url || row.image_path ? (
                <img src={getImageUrl(row.image_url || row.image_path)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Package size={14} color={tk.textMuted} />
              )}
            </div>
            <span style={{
              fontWeight: 600, fontSize: 13,
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{row.name}</span>
          </div>
        );
      case 'sku': {
        const sku = formatSkuDisplay(row.sku);
        return (
          <span
            title={sku.title}
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11.5,
              color: tk.textMuted,
              direction: 'ltr',
              unicodeBidi: 'isolate',
              display: 'inline-block',
            }}
          >
            {sku.display}
          </span>
        );
      }
      case 'selling_price':
        return <MonoMoney amount={parseFloat(row.selling_price) || 0} tk={tk} size={12.5} weight={600} />;
      case 'preview_cost':
        return <MonoMoney amount={parseFloat(row.preview_cost) || 0} tk={tk} size={12} weight={500} color={tk.textSecondary} />;
      case 'margin': {
        const selling = parseFloat(row.selling_price) || 0;
        const cost = parseFloat(row.preview_cost) || 0;
        const marginPct = cost > 0 ? ((selling - cost) / cost) * 100 : null;
        return (
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 600,
            color: marginPct != null && marginPct < 0 ? tk.dangerText : tk.successText,
            fontSize: 12,
            direction: 'ltr',
            unicodeBidi: 'isolate',
            display: 'inline-block',
          }}
          >
            {formatMarkupPercent(marginPct)}
          </span>
        );
      }
      case 'status':
        return renderOfferStatus(row);
      case 'actions':
        return (
          <div style={{ display: 'flex', gap: 6, justifyContent: isRTL ? 'flex-start' : 'flex-end', flexWrap: 'nowrap' }}>
            <button
              type="button"
              onClick={() => setViewingOffer(row)}
              style={actionBtnStyle(tk.surfaceAlt, tk.border, tk.textSecondary)}
              title={t('offers.viewOffer')}
            >
              <Eye size={13} />
            </button>
            {canManage && (
              <>
                <button type="button" onClick={() => openEdit(row)} style={actionBtnStyle(tk.surfaceAlt, tk.border, tk.textSecondary)} title={t('offers.editOffer')}>
                  <Edit size={13} />
                </button>
                <button type="button" onClick={() => setDeletingOffer(row)} style={actionBtnStyle(tk.dangerSoft, `${tk.danger}33`, tk.dangerText)} title={t('offers.deleteOffer')}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        );
      default:
        return row[key] || '—';
    }
  };

  return (
    <div>
      <style>{filterSelect.css}</style>
      {loading ? (
        <StatStripSkeleton tk={tk} count={3} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: t('offers.totalOffers'), value: String(offerStats.total) },
            { label: t('offers.activeOffers'), value: String(offerStats.active) },
            { label: t('offers.inactiveOffers'), value: String(offerStats.inactive) },
          ]}
        />
      )}
      <Toolbar
        tk={tk}
        searchPlaceholder={t('offers.searchOffers')}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        filters={
          <>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={filterSelect.className(statusFilter !== 'active')}
              data-active={filterSelect.dataActive(statusFilter !== 'active')}
            >
              <option value="all">{t('settings.allStatuses')}</option>
              <option value="active">{t('settings.active')}</option>
              <option value="inactive">{t('settings.inactive')}</option>
            </select>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  height: 34,
                  padding: '0 10px',
                  borderRadius: 8,
                  background: tk.dangerSoft,
                  border: 'none',
                  color: tk.dangerText,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <X size={12} />
                {t('common.clear')}
              </button>
            )}
          </>
        }
      />

      {loading ? (
        <TableSkeleton tk={tk} rows={8} cols={6} />
      ) : (
        <>
          <DataTable
            tk={tk}
            columns={columns}
            rows={paginated}
            renderCell={renderCell}
            emptyMessage={hasActiveFilters ? t('inventory.tryDifferentSearch') : t('offers.noOffers')}
          />
          <Pagination
            tk={tk}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {showModal && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: `1px solid ${tk.border}`,
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
                {editingOffer ? t('offers.editOffer') : t('offers.addOffer')}
              </span>
              <button type="button" onClick={closeModal} style={modalCloseBtn}>
                <X size={14} />
              </button>
            </div>

            <div style={{
              padding: 20,
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}>
              <div>
                <label style={labelStyle}>{t('offers.offerName')}</label>
                <input
                  style={inputStyle}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  maxLength={OFFER_NAME_MAX_LENGTH}
                />
              </div>

              <div>
                <label style={labelStyle}>{t('inventory.skuOptional')}</label>
                <input
                  style={inputStyle}
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value.replace(/\s/g, '') })}
                  maxLength={50}
                />
              </div>

              <div style={{
                border: `1px solid ${tk.border}`,
                borderRadius: 10,
                padding: 14,
                background: tk.surfaceAlt,
              }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input
                    type="text"
                    value={productSearchInput}
                    onChange={(e) => {
                      setProductSearchInput(e.target.value);
                      setProductSearchError(null);
                      setProductSearchResults([]);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleProductSearch(e)}
                    placeholder={t('offers.productSearchPlaceholder')}
                    maxLength={50}
                    style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                  />
                  <button
                    type="button"
                    onClick={handleProductSearch}
                    disabled={productSearchLoading || !productSearchInput.trim()}
                    style={{
                      height: 40, padding: '0 14px', borderRadius: 8,
                      background: tk.accent, border: 'none',
                      color: '#fff',
                      cursor: productSearchLoading || !productSearchInput.trim() ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                      opacity: productSearchLoading || !productSearchInput.trim() ? 0.6 : 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {productSearchLoading ? t('common.loading') : t('common.search')}
                  </button>
                </div>

                {productSearchError && (
                  <p style={{ fontSize: 12, marginBottom: 8, color: tk.dangerText }}>{productSearchError}</p>
                )}
                {productSearchResults.length > 0 && (
                  <div style={{
                    marginBottom: 12, maxHeight: 192, overflowY: 'auto',
                    border: `1px solid ${tk.border}`, borderRadius: 8,
                    background: tk.surface,
                  }}>
                    {productSearchResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleAddProductFromSearch(p)}
                        style={{
                          display: 'block', width: '100%', textAlign: isRTL ? 'right' : 'left',
                          padding: '10px 14px', background: 'transparent', border: 'none',
                          borderBottom: `1px solid ${tk.border}`, cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 500, color: tk.text }}>{p.name}</span>
                        {p.sku && (
                          <span style={{ fontSize: 11.5, color: tk.textMuted }}>
                            {` ${p.sku}`}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: tk.textSecondary,
                  marginBottom: 7,
                }}>
                  {t('offers.components')} ({formData.items.length})
                </div>

                {formData.items.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 101px 56px 100px 36px',
                    gap: 8,
                    marginBottom: 8,
                    fontSize: 10,
                    fontWeight: 600,
                    color: tk.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}>
                    <span>{t('common.product')}</span>
                    <span>{t('inventory.costPrice')}</span>
                    <span>{t('inventory.unitType')}</span>
                    <span>{t('common.quantity')}</span>
                    <span />
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {formData.items.length === 0 ? (
                    <p style={{ fontSize: 12.5, color: tk.textMuted, margin: 0 }}>
                      {t('offers.noComponents')}
                    </p>
                  ) : formData.items.map((row, idx) => {
                    const unitType = rowUnitType(row.unit_type);
                    const unitCost = parseFloat(row.cost_price) || 0;
                    const qty = parseFloat(row.quantity) || 0;
                    const lineCost = Math.round(qty * unitCost * 100) / 100;
                    return (
                      <div
                        key={row.product_id || idx}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 101px 56px 100px 36px',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: tk.text,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {row.product_name || '—'}
                          </div>
                          {row.sku && (
                            <div style={{
                              fontSize: 11,
                              fontFamily: '"JetBrains Mono", monospace',
                              color: tk.textMuted,
                              marginTop: 2,
                            }}>
                              {row.sku}
                            </div>
                          )}
                        </div>
                        <div
                          title={`${formatCurrency(unitCost)} / ${formatProductUnitType(unitType, t)}`}
                          style={{
                            height: 40,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: '0 8px',
                            background: tk.sunken,
                            border: `1px solid ${tk.border}`,
                            borderRadius: 8,
                            fontSize: 11,
                            color: tk.textSecondary,
                            lineHeight: 1.3,
                          }}
                        >
                          <span style={{ fontWeight: 600, color: tk.text }}>
                            {formatCurrency(unitCost)}
                          </span>
                          {qty > 0 && (
                            <span style={{ fontSize: 10, color: tk.textMuted }}>
                              = {formatCurrency(lineCost)}
                            </span>
                          )}
                        </div>
                        <div style={{
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 6px',
                          background: tk.sunken,
                          border: `1px solid ${tk.border}`,
                          borderRadius: 8,
                          fontSize: 11,
                          fontWeight: 600,
                          color: tk.textSecondary,
                        }}>
                          {formatProductUnitType(unitType, t)}
                        </div>
                        <input
                          style={inputStyle}
                          type="text"
                          inputMode={unitType === 'kg' ? 'decimal' : 'numeric'}
                          value={row.quantity}
                          title={unitType === 'kg' ? t('inventory.perKg') : t('inventory.perPiece')}
                          onKeyDown={(e) => {
                            if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                          }}
                          onChange={(e) => {
                            const qty = normalizePurchaseQuantityInput(e.target.value, unitType);
                            // numeric(9,3): max 999999.999
                            if (qty !== '' && parseFloat(qty) > 999999.999) return;
                            const next = [...formData.items];
                            next[idx] = {
                              ...next[idx],
                              quantity: qty,
                            };
                            setFormData({ ...formData, items: next });
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, items: formData.items.filter((_, i) => i !== idx) })}
                          style={{
                            width: 36,
                            height: 40,
                            borderRadius: 8,
                            border: `1px solid ${tk.border}`,
                            background: tk.dangerSoft,
                            color: tk.dangerText,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>{t('offers.previewCost')} ({t('currency.egp')})</label>
                  <input
                    style={{ ...inputStyle, opacity: 0.75, cursor: 'default' }}
                    readOnly
                    value={formatCurrency(previewCost)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t('inventory.sellingPrice')} ({t('currency.egp')})</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="decimal"
                    value={formData.selling_price}
                    onChange={(e) => {
                      const next = normalizeMoneyInput(e.target.value);
                      // numeric(10,2): max 99999999.99
                      if (next !== '' && parseFloat(next) > 99999999.99) return;
                      setFormData({ ...formData, selling_price: next });
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>{t('inventory.profitMargin')} ({t('currency.egp')})</label>
                <input
                  style={{
                    ...inputStyle,
                    opacity: 0.75,
                    cursor: 'default',
                    direction: 'ltr',
                    unicodeBidi: 'isolate',
                    textAlign: isRTL ? 'right' : 'left',
                    fontWeight: 600,
                    color: formMarginAmount < 0 ? tk.dangerText : formMarginAmount > 0 ? tk.successText : tk.text,
                  }}
                  readOnly
                  value={formatSignedCurrency(formMarginAmount)}
                />
              </div>

              <div>
                <label style={labelStyle}>{t('inventory.imageOptional')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {formData.image_url && (
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      height: 120,
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: `1px solid ${tk.border}`,
                      background: tk.sunken,
                    }}>
                      <img
                        src={getImageUrl(formData.image_url)}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tk.sunken,
                        flexDirection: 'column',
                        gap: 4,
                      }}>
                        <Image size={24} style={{ color: tk.textMuted }} />
                        <p style={{ fontSize: 11, color: tk.textMuted, margin: 0 }}>{t('inventory.invalidImage')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        style={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          background: tk.danger,
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <X size={12} style={{ color: '#fff' }} />
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={formData.image_url || ''}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      style={{ ...inputStyle, flex: 1 }}
                      placeholder={t('inventory.enterImageUrl')}
                    />
                    <label style={{
                      height: 40,
                      padding: '0 12px',
                      borderRadius: 8,
                      background: tk.surfaceAlt,
                      border: `1px solid ${tk.border}`,
                      color: tk.textSecondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: 'inherit',
                      fontSize: 12.5,
                      whiteSpace: 'nowrap',
                    }}>
                      <Upload size={14} />
                      <span>{t('inventory.browse')}</span>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const imageUrl = await saveImageToServer({ file, subdir: 'offers' });
                            setFormData({ ...formData, image_url: imageUrl });
                          } catch (err) {
                            showToast(
                              t('alerts.failedToSave').replace('{error}', err.message || t('alerts.unknownError')),
                              'error',
                            );
                          } finally {
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                  </div>
                  <p style={{ fontSize: 11, color: tk.textMuted, margin: 0 }}>{t('inventory.imageUrlHelp')}</p>
                </div>
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                background: tk.sunken,
                borderRadius: 8,
                border: `1px solid ${tk.border}`,
                fontSize: 13,
                color: tk.text,
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: tk.success }}
                />
                <span>{t('offers.activeOffer')}</span>
              </label>
            </div>

            <div style={{
              display: 'flex',
              gap: 10,
              padding: '14px 20px',
              borderTop: `1px solid ${tk.border}`,
            }}>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  flex: 1,
                  height: 38,
                  borderRadius: 8,
                  background: tk.surfaceAlt,
                  border: `1px solid ${tk.border}`,
                  color: tk.textSecondary,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                style={{
                  flex: 1,
                  height: 38,
                  borderRadius: 8,
                  background: tk.accent,
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingOffer && (
        <div style={overlay}>
          <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, padding: 24, maxWidth: 400, margin: '0 16px' }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>{t('offers.deleteOffer')}</div>
            <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 20 }}>{t('offers.deleteConfirm').replace('{name}', deletingOffer.name)}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setDeletingOffer(null)}
                style={{
                  flex: 1, height: 38, borderRadius: 8,
                  background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                  color: tk.textSecondary, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                }}
              >
                {t('common.cancel')}
              </button>
              <button type="button" onClick={() => handleDelete(deletingOffer)} style={{
                flex: 1, height: 38, borderRadius: 8, background: tk.danger, border: 'none',
                color: '#fff', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
              }}>
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingOffer && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: `1px solid ${tk.border}`,
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>{t('offers.offerDetails')}</span>
              <button type="button" onClick={() => setViewingOffer(null)} style={modalCloseBtn}>
                <X size={14} />
              </button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 83, height: 83, borderRadius: 10, flexShrink: 0,
                  background: tk.sunken, overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {viewingOffer.image_url || viewingOffer.image_path ? (
                    <img src={getImageUrl(viewingOffer.image_url || viewingOffer.image_path)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Package size={32} style={{ color: tk.textMuted }} />
                  )}
                </div>
                <div>
                  <p style={{ fontSize: 16, fontWeight: 600, color: tk.text, margin: '0 0 2px' }}>{viewingOffer.name}</p>
                  {viewingOffer.sku && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <p style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono", monospace', margin: 0 }}>
                        SKU: {viewingOffer.sku}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(viewingOffer.sku);
                          setSkuCopied(true);
                          setTimeout(() => setSkuCopied(false), 1500);
                        }}
                        style={{
                          width: 22, height: 22, borderRadius: 4,
                          background: 'transparent', border: 'none',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: tk.textMuted,
                        }}
                        title={t('common.copy')}
                      >
                        <Copy size={12} />
                      </button>
                      {skuCopied && (
                        <span style={{ fontSize: 11, color: tk.successText, fontWeight: 600 }}>{t('common.copied')}</span>
                      )}
                    </div>
                  )}
                  <StatusPill tk={tk} kind={viewingOffer.is_active !== false ? 'active' : 'closed'}>
                    {viewingOffer.is_active !== false ? t('settings.active') : t('settings.inactive')}
                  </StatusPill>
                </div>
              </div>

              <div>
                <div style={{ ...labelStyle, marginBottom: 10, fontWeight: 700 }}>
                  {t('offers.components')} ({(viewingOffer.items || viewingOffer.components || []).length})
                </div>
                {(viewingOffer.items || viewingOffer.components || []).length === 0 ? (
                  <p style={{ fontSize: 13, color: tk.textMuted, margin: 0 }}>{t('offers.noComponents')}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 80px 56px 72px 80px',
                      gap: 8,
                      fontSize: 10,
                      fontWeight: 600,
                      color: tk.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      padding: '0 12px',
                    }}>
                      <span>{t('common.product')}</span>
                      <span>{t('inventory.costPrice')}</span>
                      <span>{t('inventory.unitType')}</span>
                      <span>{t('common.quantity')}</span>
                      <span style={{ textAlign: isRTL ? 'left' : 'right' }}>{t('offers.lineCost')}</span>
                    </div>
                    {(viewingOffer.items || viewingOffer.components || []).map((comp, idx) => {
                      const qty = parseFloat(comp.quantity) || 0;
                      const unitCost = parseFloat(comp.cost_price) || 0;
                      const lineCost = Math.round(qty * unitCost * 100) / 100;
                      const stockQty = parseFloat(comp.stock_quantity);
                      const stockDisplay = Number.isFinite(stockQty) ? stockQty : 0;
                      const stockUnit = formatProductUnitType(comp.unit_type, t);
                      return (
                        <div
                          key={comp.product_id || idx}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 80px 56px 72px 80px',
                            gap: 8,
                            alignItems: 'center',
                            padding: '10px 12px',
                            background: tk.sunken,
                            borderRadius: 8,
                            border: `1px solid ${tk.border}`,
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: tk.text, minWidth: 0 }}>
                            {comp.product_name || '—'}{' '}
                            <span style={{
                              fontWeight: 500,
                              color: tk.textMuted,
                              whiteSpace: 'nowrap',
                              display: 'inline-flex',
                              alignItems: 'baseline',
                              direction: 'ltr',
                              unicodeBidi: 'isolate',
                            }}>
                              <span>(</span>
                              {isRTL ? (
                                <>
                                  <span>{stockUnit}</span>
                                  <span style={{ marginLeft: 4 }}>{stockDisplay}</span>
                                </>
                              ) : (
                                <>
                                  <span>{stockDisplay}</span>
                                  <span style={{ marginLeft: 4 }}>{stockUnit}</span>
                                </>
                              )}
                              <span>)</span>
                            </span>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: tk.textSecondary }}>
                            {formatCurrency(unitCost)}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: tk.textSecondary }}>
                            {formatProductUnitType(comp.unit_type, t)}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: tk.text }}>
                            {qty}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: tk.text, textAlign: isRTL ? 'left' : 'right' }}>
                            {formatCurrency(lineCost)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column' }}>
                {(() => {
                  const selling = parseFloat(viewingOffer.selling_price) || 0;
                  const cost = parseFloat(viewingOffer.preview_cost) || 0;
                  const marginAmount = selling - cost;
                  const numericValueStyle = {
                    fontWeight: 600,
                    direction: 'ltr',
                    unicodeBidi: 'isolate',
                    display: 'inline-block',
                  };
                  const pricingRows = [
                    { label: t('offers.previewCost'), value: formatCurrency(viewingOffer.preview_cost), color: tk.text },
                    { label: t('inventory.sellingPrice'), value: formatCurrency(viewingOffer.selling_price), color: tk.text },
                    {
                      label: t('inventory.profitMargin'),
                      value: formatSignedCurrency(marginAmount),
                      color: marginAmount < 0 ? tk.dangerText : marginAmount > 0 ? tk.successText : tk.text,
                    },
                  ];
                  return pricingRows.map((row, idx, arr) => (
                    <div
                      key={row.label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        fontSize: 13,
                        padding: '10px 0',
                        borderBottom: idx < arr.length - 1 ? `1px solid ${tk.border}` : 'none',
                        ...(idx === 0 ? { paddingTop: 0 } : {}),
                        ...(idx === arr.length - 1 ? { paddingBottom: 0 } : {}),
                      }}
                    >
                      <span style={{ color: tk.textMuted }}>{row.label}</span>
                      <span style={{ ...numericValueStyle, color: row.color }}>{row.value}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {canManage && (
              <div style={{
                display: 'flex',
                gap: 10,
                padding: '14px 20px',
                borderTop: `1px solid ${tk.border}`,
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setViewingOffer(null);
                    openEdit(viewingOffer);
                  }}
                  style={{
                    flex: 1,
                    height: 38,
                    borderRadius: 8,
                    background: tk.surfaceAlt,
                    border: `1px solid ${tk.border}`,
                    color: tk.text,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <Edit size={14} />
                  {t('offers.editOffer')}
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleActive(viewingOffer)}
                  disabled={togglingId === viewingOffer.id}
                  style={{
                    flex: 1,
                    height: 38,
                    borderRadius: 8,
                    border: viewingOffer.is_active !== false ? `1px solid ${tk.warning}33` : 'none',
                    cursor: togglingId === viewingOffer.id ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    background: viewingOffer.is_active !== false ? tk.warningSoft : tk.success,
                    color: viewingOffer.is_active !== false ? tk.warningText : '#fff',
                    opacity: togglingId === viewingOffer.id ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  {viewingOffer.is_active !== false ? t('offers.deactivate') : t('offers.activate')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default OffersPanel;
